<!-- eslint-disable vue-i18n/no-dynamic-keys -->
<template>
  <page class="register-questions" :loading="!company" :err="fatalErr">
    <template v-slot:header><header-minimal/></template>
    <b-row v-if="company">
      <b-col cols="12" md="10" offset-md="1">
        <b-card class="shadow mx-md-10 px-1 px-md-10 py-2 mb-5">

          <b-row class="pt-5 pb-4 px-5">
            <b-col lg="4" class="px-4">
              <div class="py-3 px-3">
                <b-img v-if="company.branding && company.branding.registerImage && company.branding.registerImage.fileLocation"
                  :src="company.branding.registerImage.fileLocation" fluid/>
                <b-img v-else src="/static/images/illustrations/1.svg" fluid/>
              </div>
            </b-col>
            <b-col lg="8" class="px-4">

              <h3 class="mb-4">{{ $t('onboarding.register-questions.title') }} </h3>

              <b-form-group v-if="canSelectDepartment"
                :label="departmentLabel || $t('onboarding.register-questions.department-label')" label-for="formDepartment">
                  <b-form-select id="formDepartment" :options="selectDepartmentOptions" v-model="selectedDepartmentID">
                    <template v-slot:first><option :value="null">{{ $t('ctas.select-option') }}</option></template>
                  </b-form-select>
              </b-form-group>

              <b-form-group v-if="canSelectLocation" :label="$t('onboarding.register-questions.location-label')" label-for="formLocation">
                <location-search id="formLocation" v-model="selectedLocation"/>
              </b-form-group>

              <div v-for="({ key, name, description, type, options, departments, required }) in customFields" :key="key">
                <div v-if="['STRING', 'ENUM', 'LOCATION'].includes(type) && (!departments || departments.filter(id => departmentIds.includes(id)).length)">
                  <b-form-group
                    :label-for="`input-${key}`"
                    :label="typeof name === 'string' && name.match(/^onboardingDOTi18nDOT.+$/) ? $t(name.replaceAll('DOT', '.')) : name"
                    :description="typeof description === 'string' && description.match(/^onboardingDOTi18nDOT.+$/) ? $t(description.replaceAll('DOT', '.')) : description"
                  >
                    <b-form-select
                      v-if="type === 'ENUM' && options"
                      :id="`input-${key}`"
                      type="text"
                      :required="required"
                      :options="options"
                      :value="inputValues[key]"
                      @input="value => inputValues[key] = value"
                    >
                        <template v-slot:first><option :value="null">{{ $t('ctas.select-option') }}</option></template>
                    </b-form-select>
                    <b-form-input
                      v-if="type === 'STRING'"
                      :id="`input-${key}`"
                      type="text"
                      :required="required"
                      :value="inputValues[key]"
                      @input="value => inputValues[key] = value"
                    />
                  </b-form-group>
                </div>
                <job-title-selector v-if="type === 'JOB_TITLES'"
                  :value="inputValues[key]"
                  @input="value => inputValues[key] = value"
                  :name="name"
                  :description="description"
                  :options="options"
                  :required="required"
                  :settings="{ noHeader: true }"/>
                <job-skills-selector v-if="type === 'JOB_SKILLS' || type === 'JOB_DEVELOPMENT_SKILLS'"
                  :value="inputValues[key]"
                  @input="value => inputValues[key] = value"
                  :name="name"
                  :description="description"
                  :options="options"
                  :required="required"/>
                <languages-selector v-if="type === 'LANGUAGES'"
                  :value="inputValues[key]"
                  @input="value => inputValues[key] = value"
                  :name="name"
                  :description="description"
                  :required="required"/>
                <qualifications-editor v-if="type === 'QUALIFICATIONS'"
                  :value="inputValues[key]"
                  @input="value => inputValues[key] = value"
                  :name="name"
                  :description="description"
                  :required="required"/>
                <date-editor v-if="type === 'DATE'"
                  :value="inputValues[key]"
                  @input="value => inputValues[key] = value"
                  :name="name"
                  :description="description"
                  :required="required"/>
                <job-history-editor v-if="type === 'JOB_HISTORY'"
                  :value="inputValues[key]"
                  @input="value => inputValues[key] = value"
                  :name="name"
                  :description="description"
                  :required="required"/>
              </div>
            </b-col>
          </b-row>

          <b-row class="px-5">
            <b-col class="d-flex justify-content-end align-items-center px-4">
              <b-button @click.prevent="submitForm" class="px-5" variant="primary">{{ $t('ctas.continue') }}</b-button>
            </b-col>
          </b-row>
        </b-card>
      </b-col>
    </b-row>
  </page>
</template>

<script>
import Page from '@/components/Page';
import authMixin from '@src/mixins/auth';
import HeaderMinimal from '@/components/Header-minimal';
import FooterMinimal from '@/components/Footer-minimal';
import LocationSearch from '@/components/misc/LocationSearch';
import RegisterQuestionsQuery from '@/graphql/queries/RegisterQuestions.gql';
import gql from 'graphql-tag';
import ErrorBlock from '@/components/misc/ErrorBlock';
import { mapRoot } from '@src/mixins/root';

const SaveUserProfileMutation = gql`
  mutation SaveUserProfile($userID: ID!, $update: UpdateUserInput!) {
    updateUser(update: $update, where: { id: $userID }) {
      id
      departments {
        id
        name
        icon
      }
      location {
        id
        name
      }
    }
  }
`;

const WriteUserQuestionsMutation = gql`
  mutation WriteUserCustomFields($update: [ UpdateUserCustomFieldInput! ]!) {
    updateManyUserCustomFields(update: $update) {
      key
      value
    }
  }
`;

export default {
  components: {
    ErrorBlock,
    Page,
    HeaderMinimal,
    FooterMinimal,
    LocationSearch,
  },
  mixins: [ authMixin ],
  data: () => ({
    fatalErr: null,
    err: null,
    selectedDepartmentID: null,
    selectedLocation: null,
    inputValues: {},
  }),
  apollo: {
    company: {
      query: RegisterQuestionsQuery,
      update: ({ company }) => company,
      result({ loading, data }) {
        if (!loading && data && data.company) {
          const { company, user } = data;
          this.$assert(company && company.id, 500, new Error('Failed to load company register questions'));
          this.$assert(user && user.id, 500, new Error('Failed to load user register questions'));

          if (!this.canSelectDepartment && !this.canSelectLocation && !this.customFields.length) {
            return this.$router.push(this.nextRoute || { name: this.isRecruiter ? 'Dashboard' : 'Onboarding' });
          }

          if (this.canSelectDepartment && user && Array.isArray(user.departments) && user.departments.length) {
            this.selectedDepartmentID = user.departments[0].id;
          }
          if (this.canSelectLocation && user && user.location && user.location.id) {
            this.selectedLocation = user.location;
          }
          if (user && Array.isArray(user.customFields) && user.customFields.length) {
            this.inputValues = user.customFields.reduce((values, { key, value }) => Object.assign(values, { [key]: value }), {});
          }
        }
      },
      error(err) {
        this.fatalErr = err;
      }
    },
  },
  metaInfo() {
    return {
      title: this.$t('onboarding.register-questions.title'),
    };
  },
  computed: {
    ...mapRoot([ 'user', 'isRecruiter', 'departmentLabel' ]),
    canSelectDepartment() {
      const { departments, preferences: { askUserForDepartment = false } = {} } = this.company || {};
      return Array.isArray(departments) && departments.length && askUserForDepartment;
    },
    canSelectLocation() {
      const { preferences: { askUserForLocation = false } = {} } = this.company || {};
      return askUserForLocation;
    },
    customFields() {
      if (this.company && this.company.handle === 'hellofresh' &&
        this.user && this.user.emailAddress && this.user.emailAddress.match(/@hellofresh\./)) {
        return [];
      }

      const { customFields } = this.company || {};
      return (Array.isArray(customFields) ? customFields : []).map(({ options, departments, ...field }) => ({
        ...field,
        options: options ? JSON.parse(options) : null,
        departments: departments ? departments.map(({ id }) => id) : null,
      })).filter(({ group }) => group === 'registration');
    },
    selectDepartmentOptions() {
      const { departments } = this.company || {};
      const options = Array.isArray(departments) ? departments.map(({ id, name }) => ({ text: name, value: id })) : [];
      return options.sort((a, b) => a.text.localeCompare(b.text));
    },
  },
  methods: {
    async submitForm() {
      try {
        let nextRoute = null;

        const { inputValues } = this;

        await this.$apollo.mutate({
          mutation: SaveUserProfileMutation,
          variables: {
            userID: this.user.id,
            update: {
              departments: this.selectedDepartmentID ? { id: this.selectedDepartmentID } : null,
              location: this.selectedLocation && this.selectedLocation.id ? { id: this.selectedLocation.id } : null,
            },
          },
          update(store, { data: { updateUser: { departments, location } } }) {
            const data = store.readQuery({ query: RegisterQuestionsQuery });
            data.user.departments = departments;
            data.user.location = location;
            store.writeQuery({ query: RegisterQuestionsQuery, data });
          },
        });

        if (Object.keys(inputValues).length) {
          await this.$apollo.mutate({
            mutation: WriteUserQuestionsMutation,
            variables: {
              update: Object.keys(inputValues).map(key => ({ key, value: inputValues[key] })),
            },
            update(store, { data: { updateManyUserCustomFields: fields } }) {
              const data = store.readQuery({ query: RegisterQuestionsQuery });
              data.user.customFields = data.user.customFields.map(field => {
                if (inputValues[field.key]) {
                  field.value = inputValues[field.key];
                }
                return field;
              });
              store.writeQuery({ query: RegisterQuestionsQuery, data });
            },
          });
        }

        if (this.$root.isCommunitySite && this.$root.isCommunityApplicant) {
          const { query } = this.$route;
          nextRoute = { name: 'Dashboard', query };
        }

        await this.afterSignup(nextRoute || this.nextRoute || null);
      } catch (err) {
        console.error(err);
        this.err = err;
      }
    },
  },
};
</script>



// WEBPACK FOOTER //
// src/views/Auth/RegisterQuestions.vue